import {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  memo,
  useRef,
  useMemo
} from 'react'

import { useGeneral } from '../../contexts/GeneralContext'

import { FileWithPreview, UploadAreaResponse } from './UploadBase/helpers'
import UploadPrimary, { UploadMethods } from './UploadBase'

import { translation } from './translation'
import { uploadMultipleFiles } from '../../lib/upload'

interface UploadAreaProps {
  data?: string[] //Must be an array
  props: {
    type: 'doc' | 'img' | 'video' | 'spreadsheet' | 'mix' | 'certificate'
    maxSize: number // Number of megabytes
    maxQuantity: number // Max. number of files
    title?: string
    description?: string // Description shown on upload area
    minWidth?: string
    label?: string
    fullPreview?: boolean
    minified?: boolean
    small?: boolean
    status?: 'pending' | 'processing' | 'error' | 'ready' | null
    required?: boolean
  }
  callback?: (data: UploadAreaResponse) => void
}

export interface UploadAreaMethods {
  handleSave: () => Promise<UploadAreaResponse | null>
  getActualFiles: () => string[]
  getNewFiles: () => FileWithPreview[]
  clearFiles: () => void
  replaceFiles: (urls: string[]) => void
}

const UploadAreaMembersArea = forwardRef<UploadAreaMethods, UploadAreaProps>(
  ({ data: originalData, props, callback }, ref) => {
    const uploadRef = useRef<UploadMethods>(null)
    const [data, setData] = useState<string[]>(
      originalData ? JSON.parse(JSON.stringify(originalData)) : []
    )
    const { alertError } = useGeneral()
    const strings = translation['pt']
    const [deletedFiles, setDeletedFiles] = useState<string[]>([])
    const [newFiles, setNewFiles] = useState<FileWithPreview[]>([])
    const stabilizedProps = useMemo(
      () => ({ ...props, minWidth: props.minWidth || '100%' }),
      [props]
    )

    useImperativeHandle(
      ref,
      () => ({
        async handleSave() {
          const newFilesUrls: string[] = []
          if (newFiles.length > 0) {
            const uploadRes = await uploadMultipleFiles(newFiles)

            if (uploadRes.error) {
              alertError('error', strings.error, strings.defaultError)
              return null
            }

            const validUrls = uploadRes.body.map(url => url.url)
            newFilesUrls.push(...validUrls)
          }

          const finalFiles: string[] = [
            ...data.filter(e => !deletedFiles.includes(e)),
            ...newFilesUrls
          ]
          return { newFiles: newFilesUrls, deletedFiles, finalFiles }
        },
        getActualFiles() {
          const finalFiles: string[] = [
            ...data.filter(e => !deletedFiles.includes(e)),
            ...newFiles.map(e => e.preview)
          ]
          return finalFiles
        },
        getNewFiles() {
          return newFiles
        },
        async clearFiles() {
          setData([])
          setDeletedFiles([])
          setNewFiles([])
          uploadRef.current?.clearFiles()
        },
        async replaceFiles(url: string[]) {
          uploadRef.current?.clearFiles()
          setData([...url])
        }
      }),
      [newFiles, data, deletedFiles, uploadRef]
    )

    useEffect(() => {
      if (callback) {
        callback({
          newFiles: newFiles.map(e => e.preview),
          deletedFiles,
          finalFiles: [
            ...data.filter(e => !deletedFiles.includes(e)),
            ...newFiles.map(e => e.preview)
          ]
        })
      }
    }, [newFiles, data, deletedFiles])

    useEffect(() => {
      if (originalData) {
        setData([...originalData])
      }
    }, [originalData])

    return (
      <UploadPrimary
        ref={uploadRef}
        data={data}
        setData={setData}
        setDeletedFiles={setDeletedFiles}
        setNewFiles={setNewFiles}
        props={stabilizedProps}
      />
    )
  }
)

export default memo(UploadAreaMembersArea)
